import React, { useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Paper,
  InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getTypeLightColor } from "../../../../constants/colors";

export default function SearchBar({
  onInputChange,
  onSearch,
  onOpenFilter,
  onClearSearch,
  searchInputText,
  type = "project",
}) {
  const iconColor = getTypeLightColor(type);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const searchInputElementRef = useRef(null);
  const focusEscapeElementRef = useRef(null);

  // ボタンのデザイン
  const paperBg = "#fff";
  const filterHoverBg = "rgba(0,0,0,0.18)";

  const filterBtnSx = {
    width: 44,
    height: 44,
    p: 0,
    color: iconColor,
    borderRadius: "50%",
    backgroundColor: paperBg,
    "&:hover": { backgroundColor: filterHoverBg },
  };

  const closeMobileKeyboard = () => {
    searchInputElementRef.current?.blur();
  };

  return (
    <Box sx={{ py: 2 }}>
      <button
        ref={focusEscapeElementRef}
        type="button"
        tabIndex={-1}
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Paper
          variant="outlined"
          sx={{
            height: "44px",
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 1,
            pl: 1,
            pr: 0,
            touchAction: "manipulation",
            position: "relative",
            zIndex: 10,
            backgroundColor: "#fff",
            borderRadius: "999px",
            border: "none",
          }}
        >
          {/* 左固定 */}
          <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
            <SearchIcon fontSize="medium" sx={{ color: iconColor }} />
          </Box>

          {/* 中央：入力欄 */}
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <TextField
              inputRef={searchInputElementRef}
              value={searchInputText}
              onChange={onInputChange}
              type="search"
              placeholder="検索したいキーワードを入力"
              inputProps={{ "aria-label": "検索キーワード" }}
              onKeyDown={(event) => {
                if (event.nativeEvent?.isComposing) return;
                // Enterで検索実行（入力は消さない）
                if (event.key !== "Enter") return;
                event.preventDefault();
                event.stopPropagation();
                onSearch?.();
                // スマホ・タブレットなどタッチデバイスではキーボードを閉じる
                const hasTouch =
                  typeof window !== "undefined" &&
                  ("ontouchstart" in window ||
                    (typeof navigator !== "undefined" &&
                      (navigator.maxTouchPoints > 0 ||
                        // `msMaxTouchPoints` は古いブラウザ向けのフォールバック
                        navigator.msMaxTouchPoints > 0)));
                const shouldCloseKeyboard = isMobile || hasTouch;
                if (shouldCloseKeyboard) {
                  closeMobileKeyboard();
                }
              }}
              variant="outlined"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                  borderRadius: "999px",
                  backgroundColor: "transparent",
                  paddingRight: "0px !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-root.Mui-focused": {
                  boxShadow: "none !important",
                },
                "& input": {
                  outline: "none !important",
                  boxShadow: "none !important",
                },
                "&:focus-within": {
                  boxShadow: "none !important",
                  outline: "none !important",
                },
                "& input::-webkit-search-cancel-button": { WebkitAppearance: "none" },
                "& input::-webkit-search-decoration": { WebkitAppearance: "none" },
                "& input::-webkit-search-results-button": { WebkitAppearance: "none" },
                "& input::-webkit-search-results-decoration": { WebkitAppearance: "none" },
              }}
              InputProps={{
                endAdornment: (searchInputText ?? "").length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="検索欄の入力をクリア"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={onClearSearch}
                      sx={{ color: theme.palette.text.disabled }}
                    >
                      <ClearRoundedIcon fontSize="medium" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {/* 右固定（スマホだけ） */}
          <Box sx={{ flex: "0 0 auto" }}>
            {isMobile ? (
              <IconButton
                size="medium"
                aria-label="絞り込み条件を開く"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => onOpenFilter?.(event)}
                sx={{ color: iconColor }}
              >
                <FilterAltRoundedIcon />
              </IconButton>
            ) : null}
          </Box>
        </Paper>

        {/* PCは検索バーの横にフィルターアイコン　 */}
        {!isMobile ? (
          <IconButton
            size="small"
            aria-label="絞り込み条件を開く"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => onOpenFilter?.(event)}
            sx={filterBtnSx}
          >
            <FilterAltRoundedIcon />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}